// Pure parsers for Firebase Storage download URLs.
//
// Kept free of the Firebase SDK so they can be unit-tested without booting a
// Firebase app. Shape of a download URL:
//   https://firebasestorage.googleapis.com/v0/b/<bucket>/o/<url-encoded path>?alt=media&token=<uuid>

const DOWNLOAD_URL_PREFIX = "https://firebasestorage.googleapis.com/";

/** Whether a stored `file_path` is a Firebase Storage download URL. */
export function isFirebaseDownloadUrl(url: string | null | undefined): boolean {
  return !!url && url.startsWith(DOWNLOAD_URL_PREFIX);
}

/**
 * The object's path inside the bucket, decoded — e.g.
 * "utility_forms_v2/<formId>/photos/<uuid>.jpg".
 *
 * Returns null for non-Firebase URLs and for URLs whose `/o/` segment can't be
 * decoded, so callers can pass foreign http URLs through unchanged.
 */
export function parseFirebaseObjectPath(url: string): string | null {
  return parseFirebaseStorageLocation(url)?.path ?? null;
}

/**
 * Bucket + object path from a download URL.
 *
 * The bucket matters: a download URL names its own bucket, and asset photos live
 * in the prod bucket regardless of which project the running app is configured
 * for. Resolving a bare path against the app's *default* bucket therefore aims
 * at the wrong object whenever the two differ — e.g. `npm run dev` configures the
 * test bucket while the photos live in `disco-outpost-268223.appspot.com`.
 */
export function parseFirebaseStorageLocation(
  url: string,
): { bucket: string; path: string } | null {
  if (!isFirebaseDownloadUrl(url)) return null;
  const match = url.match(/\/v0\/b\/([^/]+)\/o\/([^?]+)/);
  if (!match) return null;
  try {
    return { bucket: decodeURIComponent(match[1]), path: decodeURIComponent(match[2]) };
  } catch {
    return null;
  }
}

/**
 * A `gs://bucket/path` reference for a download URL, which `ref()` resolves
 * against the named bucket instead of the app's default one.
 */
export function gsUriFromDownloadUrl(url: string): string | null {
  const location = parseFirebaseStorageLocation(url);
  return location ? `gs://${location.bucket}/${location.path}` : null;
}

/**
 * The sibling thumbnail object for an image path.
 *
 * The forms upload pipeline stores a 250 px-wide JPEG next to every original —
 * `generateThumbnail` in robotic-imaging-functions does exactly this rename
 * (`services/image-processing/generate-thumbnail.ts`), and the Firestore
 * NodeFile's `thumbnailUrl` points at it. Measured on a real asset photo: 10 KB
 * versus 2.4 MB for the original, so a 26-photo asset drops from ~62 MB to
 * ~265 KB.
 *
 * Only forms photos have one. Flutter-captured asset photos under
 * `supabase_properties/…` are compressed at capture (~0.5 MB) and have no
 * thumbnail, so callers must fall back to the original.
 *
 * Returns null when the path has no extension to replace.
 */
export function thumbnailObjectPath(path: string): string | null {
  const thumb = path.replace(/(\.[\w]+)$/, "_th.jpg");
  return thumb === path ? null : thumb;
}

/**
 * Whether two stored URLs point at the same Storage object.
 *
 * The `?token=` half rotates independently of the object — an overwrite re-mints
 * it — so identity is the path. This is the identity the database dedupes on:
 * `split_part(file_path,'?',1)` (ENG-1034), which is also why rewriting a row's
 * `file_path` with a fresh token is safe.
 */
export function sameStorageObject(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  if (!a || !b) return false;
  return a.split("?")[0] === b.split("?")[0];
}
